import { useState, useEffect } from 'react';
import DOMPurify from 'dompurify';
import { logErrorUnlessNetworkFailure } from 'setup/logger/logErrorUnlessNetworkFailure';
import { logError } from 'setup/logger/logError';
import { fileKindService } from 'services/fileService';
import {
  DEFAULT_PREVIEW_PARSE_TIMEOUT_MS,
  MAX_PREVIEW_FILE_SIZE_BYTES,
} from '../../app/constants/filePreview';
import {
  WORD_DEFAULT_ALLOWED_ATTR,
  WORD_DEFAULT_ALLOWED_TAGS,
  WORD_DEFAULT_STYLE_MAP,
} from '../../app/constants/wordSanitization';
import { WordPreviewOptions } from '../../app/constants/previewSecurity';

interface UseWordDocumentResult {
  loading: boolean;
  error: boolean;
  htmlContent: string;
}

export const useWordDocument = (
  src: string | undefined,
  options?: WordPreviewOptions
): UseWordDocumentResult => {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);
  const [htmlContent, setHtmlContent] = useState<string>('');

  useEffect(() => {
    if (!src) {
      setError(true);
      setLoading(false);
      return;
    }

    const loadWord = async (): Promise<void> => {
      const timeoutId = setTimeout(() => {
        logError('Word document parsing timeout');
        setError(true);
        setLoading(false);
      }, DEFAULT_PREVIEW_PARSE_TIMEOUT_MS);

      try {
        setLoading(true);
        setError(false);

        // Fetch the document
        const response = await fetch(src, { credentials: 'include' });

        if (!response.ok) {
          throw new Error(`HTTP error! status: ${response.status}`);
        }

        // Validate file size from Content-Length header (before downloading)
        const contentLength = response.headers.get('content-length');
        if (contentLength) {
          const fileSize = parseInt(contentLength, 10);
          if (fileSize > MAX_PREVIEW_FILE_SIZE_BYTES) {
            // File too large - this is not an error, just a limitation
            clearTimeout(timeoutId);
            setError(true);
            setLoading(false);
            return;
          }
        }

        // Validate Content-Type
        const contentType = response.headers.get('content-type');
        if (
          contentType &&
          !fileKindService.isWordContentType(contentType)
        ) {
          logError('Invalid content type for Word document:', contentType);
          throw new Error('Invalid file type');
        }

        const arrayBuffer = await response.arrayBuffer();

        // Double-check file size after download (in case Content-Length was missing)
        if (arrayBuffer.byteLength > MAX_PREVIEW_FILE_SIZE_BYTES) {
          // File too large - this is not an error, just a limitation
          clearTimeout(timeoutId);
          setError(true);
          setLoading(false);
          return;
        }

        // Dynamically import mammoth only when needed
        const mammoth = await import('mammoth');

        const result = await mammoth.convertToHtml(
          { arrayBuffer },
          {
            styleMap: options?.styleMap || [...WORD_DEFAULT_STYLE_MAP],
          }
        );

        const cleanHtml = DOMPurify.sanitize(result.value, {
          ALLOWED_TAGS:
            options?.allowedTags || [...WORD_DEFAULT_ALLOWED_TAGS],
          ALLOWED_ATTR:
            options?.allowedAttr || [...WORD_DEFAULT_ALLOWED_ATTR],
          ALLOW_DATA_ATTR: false,
        });

        clearTimeout(timeoutId);
        setHtmlContent(cleanHtml);
        setLoading(false);
      } catch (err) {
        clearTimeout(timeoutId);
        logErrorUnlessNetworkFailure('Error loading Word document:', err);
        setError(true);
        setLoading(false);
      }
    };

    loadWord();
  }, [src, options?.styleMap, options?.allowedTags, options?.allowedAttr]);

  return { loading, error, htmlContent };
};
